import { Select } from '@monorepo/shared/componentsV2/Select/Select';
import React, { useCallback, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { FieldConditionHelpers } from '../../hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from '../../types';
import { DeleteConditionalPropertyButton } from '../DeleteConditionalPropertyButton';
import { ConditionallyShownFieldInfo } from './types';

export const ConditionallyShownFieldRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
`;

const AVAILABLE_GROUP_NAME = 'Available fields';
const ALREADY_IN_USE_GROUP_NAME = 'Already in use';

function getOptions(
  fields: EditableFormTemplateField[],
  groupName: string,
  { disabled }: { disabled?: boolean } = {},
) {
  return fields.map((field) => ({
    label: field.name,
    value: field.id,
    groupName,
    disabled,
  }));
}

function getUnavailableFieldOptionsWithoutSelf({
  unavailableFields,
  ownFieldId,
}: {
  unavailableFields: EditableFormTemplateField[];
  ownFieldId: string;
}) {
  const unavailableFieldsWithoutSelf = unavailableFields.filter(
    (field) => field.id !== ownFieldId,
  );
  return getOptions(unavailableFieldsWithoutSelf, ALREADY_IN_USE_GROUP_NAME, {
    disabled: true,
  });
}

function getAvailableFieldOptionsWithSelf({
  fieldConditionHelpers,
  availableFields,
  ownFieldId,
}: {
  fieldConditionHelpers: FieldConditionHelpers;
  availableFields: EditableFormTemplateField[];
  ownFieldId: string;
}) {
  const selfField = fieldConditionHelpers.getFieldById(ownFieldId);

  const unselectedOptions = getOptions(availableFields, AVAILABLE_GROUP_NAME);

  return selfField
    ? [
        {
          label: selfField.name,
          value: selfField.id,
          groupName: AVAILABLE_GROUP_NAME,
        },
        ...unselectedOptions,
      ]
    : unselectedOptions;
}

interface ConditionallyShownFieldPickerProps {
  availableFields: EditableFormTemplateField[];
  isDeletable: boolean;
  fieldConditionHelpers: FieldConditionHelpers;
  formFieldName: string;
  isFirstField: boolean;
  removeConditionallyShownField: () => void;
  unavailableFields: EditableFormTemplateField[];
}

function validateHasFieldPicked(value: ConditionallyShownFieldInfo) {
  return value?.id ? undefined : 'Pick a field.';
}

export function ConditionallyShownFieldPicker({
  availableFields,
  isDeletable,
  fieldConditionHelpers,
  formFieldName,
  isFirstField,
  removeConditionallyShownField,
  unavailableFields,
}: ConditionallyShownFieldPickerProps) {
  const {
    input: {
      value: { id: fieldId },
      onChange: fieldOnChange,
    },
    meta,
  } = useField<ConditionallyShownFieldInfo>(formFieldName, {
    validate: validateHasFieldPicked,
  });

  const onChange = useCallback(
    (newFieldId: string) => {
      const newFieldValue: ConditionallyShownFieldInfo = {
        id: newFieldId,
        hideInPdfIfConditionallyHidden: false,
      };
      fieldOnChange(newFieldValue);
    },
    [fieldOnChange],
  );

  const allOptions = useMemo(() => {
    const availableOptionsWithSelf = getAvailableFieldOptionsWithSelf({
      fieldConditionHelpers,
      availableFields,
      ownFieldId: fieldId,
    });

    const unavailableOptions = getUnavailableFieldOptionsWithoutSelf({
      unavailableFields,
      ownFieldId: fieldId,
    });

    return [...availableOptionsWithSelf, ...unavailableOptions].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [fieldConditionHelpers, availableFields, fieldId, unavailableFields]);

  const selectGroups = useMemo(
    () => [
      { name: AVAILABLE_GROUP_NAME, label: AVAILABLE_GROUP_NAME },
      {
        name: ALREADY_IN_USE_GROUP_NAME,
        label: ALREADY_IN_USE_GROUP_NAME,
        disabled: true,
        tooltip: 'These fields are already used in a condition.',
      },
    ],
    [],
  );

  const hasError = !!meta.error && (meta.touched || meta.modified);

  return (
    <ConditionallyShownFieldRow>
      <Select
        error={hasError}
        errorText={hasError ? meta.error : undefined}
        name={formFieldName}
        label={isFirstField ? 'Then show' : 'and'}
        options={allOptions}
        value={fieldId}
        onChange={(event) => onChange(event.target.value as string)}
        dontSaveSpaceForErrorText
        placeholder="Choose field"
        groups={selectGroups}
      />
      <DeleteConditionalPropertyButton
        remove={removeConditionallyShownField}
        label="Delete conditionally shown field"
        $isVisible={isDeletable}
        $isErrorTextShown={!!hasError}
      />
    </ConditionallyShownFieldRow>
  );
}
